import * as _ from 'lodash';
import { SurveyService } from '../../service/surveys/surveys.service';
 export interface SurveyObservation {
    Question: string;
    Feedback: string;
    Notes: string;
    Explanation?: string;
    Points: number;
    Scale: string;
    Step: number;
    Code: string;
  }
  
  export interface SurveyPraise {
    Question: string;
    Strength: string;
    Notes: string;
    Explanation?: string;
    Points: number;
    Scale: string;
    Step: number;
    Code: string;
  }
  
  export interface SurveyBenchmark {
    Question: string;
    Benchmark: string;
    Step: number;
    SkillLevel: string;
    Description: string;
    Points: number;
  }
  
  export interface SurveyResult {
    observations: SurveyObservation[];
    praise: SurveyPraise[];
    nubm: SurveyBenchmark[];
    iubm: SurveyBenchmark[];
    aubm: SurveyBenchmark[];
    unknown: string[];
    pscore: number;
    oscore: number;
    nscore: number;
    iscore: number;
    ascore: number;
  }
 
  export interface Survey {
    surveyId?;
    id: string;
    type?: string;
    aubm: string[];
    iubm: string[];
    nubm: string[];
    observations: string[];
    praise: string[];
    processedResults: SurveyResult;
    aiReport?: string;
    coachesReport?: string;
    raw: any;
    skill?: string;
    selectedFeedback?: string[];
    notes?: { 
      step0?: {
      coachesNote?: string,
      learnersNote?: string
      },
      step1?: {
      coachesNote?: string,
      learnersNote?: string
      },
      step2?: {
      coachesNote?: string,
      learnersNote?: string
      },
      step3?: {
      coachesNote?: string,
      learnersNote?: string
      },
      step4?: {
      coachesNote?: string,
      learnersNote?: string
      },
      step5?: {
      coachesNote?: string,
      learnersNote?: string
      }
    }
    userType?: string; 
    level?: string;  
    stepResults?: any;
  }
  
  export interface RawSurvey {
    date_submitted: string;
  }

  export class SurveyResponseProcessor {
  // variables
  survey;
  surveyResponse;
  subquestionMap: { [key: string]: any } = {};

  constructor(surveyResponse: any, survey: any) {
    this.survey = survey;
    this.surveyResponse = surveyResponse;
    this.subquestionMap = this.buildSubquestionMap();
  }

  // methods

  // Flatten every subquestion across the response into a single {id -> subquestion}
  // map. Runs once at construction so lookups are O(1).
  private buildSubquestionMap(): { [key: string]: any } {
    const map: { [key: string]: any } = {};
    if (!this.surveyResponse) return map;

    for (const questionId in this.surveyResponse) {
      const question = this.surveyResponse[questionId];
      if (question && question.subquestions) {
        for (const subId in question.subquestions) {
          map[subId] = question.subquestions[subId];
        }
      }
    }
    return map;
  }

  // Walk the survey schema for a sub_question matching an id. Used as the
  // title source for subquestions in getQuestionById.
  private findSubquestionInSurvey(subId: any): any {
    if (!this.survey || !this.survey.questions) return null;

    for (const qId in this.survey.questions) {
      const q = this.survey.questions[qId];
      if (q && q.sub_questions) {
        for (const key in q.sub_questions) {
          const sq = q.sub_questions[key];
          if (sq && sq.id === subId) return sq;
        }
      }
    }
    return null;
  }

  getId(value) {
    if (typeof value == 'number') {
      return value;
    } else {
      return this.survey.shortnames[value];
    }
  }

  hasAnswer(value) {
    // value is a question id or shortname
    var id = this.getId(value);

    if (id) {
      return true;
    } else {
      return false;
    }
  }

  public getSurveyAnswer(questionId: any, surveyData?: any) {
    if (typeof questionId == 'number' && this.surveyResponse) {
      // question
      var questionObj = this.surveyResponse[questionId];

      if (questionObj) {
        return questionObj.answer;
      } else if (this.subquestionMap[questionId]) {
        // subquestion fallback
        return this.subquestionMap[questionId].answer;
      } else {
        return "";
      }
    } else if (typeof questionId == 'string') {
      // shortname
      var id = this.survey.shortnames[questionId];
      var questionObj = this.surveyResponse[id];

      if (questionObj) {
        return questionObj.answer;
      } else if (this.subquestionMap[id]) {
        // subquestion fallback
        return this.subquestionMap[id].answer;
      } else {
        return "";
      }
    }
  }

  // Simple textbox answers etc.
  public getSurveyQuestionAndAnswer(questionId: any, surveyData: any) {
    if (questionId && surveyData) {
      var questionObj = surveyData[questionId];
      if (questionObj.question && questionObj.answer) {
        return {
          question: questionObj.question,
          answer: questionObj.answer
        };
      } else {
        return "question not found.";
      }
    } else {
      return "missing input parameters.";
    }
  }

  public getQuestionById(questionId: any) {
    if (typeof questionId == 'number' && this.survey) {
      // question
      var questionObj = this.survey.questions[questionId];

      if (questionObj) {
        return questionObj.title;
      } else {
        // subquestion fallback
        var schemaSub = this.findSubquestionInSurvey(questionId);
        if (schemaSub && schemaSub.title) {
          return schemaSub.title;
        }
        var responseSub = this.subquestionMap[questionId];
        if (responseSub && responseSub.question) {
          return responseSub.question;
        }
        return "question not found.";
      }
    } else if (typeof questionId == 'string') {
      // shortname
      var id = this.survey.shortnames[questionId];
      var questionObj = this.survey.questions[id];

      if (questionObj) {
        return questionObj.title;
      } else {
        // subquestion fallback
        var schemaSub = this.findSubquestionInSurvey(id);
        if (schemaSub && schemaSub.title) {
          return schemaSub.title;
        }
        var responseSub = this.subquestionMap[id];
        if (responseSub && responseSub.question) {
          return responseSub.question;
        }
        return "question not found.";
      }
    }

    if (questionId && this.survey) {
      var questionObj = this.survey.questions[questionId];
      if (questionObj.title) {
        return questionObj.title;
      } else {
        return "question not found.";
      }
    } else {
      return "missing input parameters.";
    }
  }

  // Get the ranks the user ordered the options. This will return an array of objects with the id, option and rank (even if rank is a blank string).
  public getRankAnswer(id: any): any {
    const survey = this.surveyResponse[id];

    if (!survey) {
      console.log(`Survey with ID ${id} not found`);
    }

    if (_.isString(survey.answer)) {
      return [{ id: parseInt(id), option: survey.answer, rank: '' }];
    } else {
      const answerObject = survey.answer;
      return Object.values(answerObject).map((answer: any) => ({
        id: answer.id,
        option: answer.option,
        rank: answer.rank
      }));
    }
  }

  // returns checkbox and options answers
  public getOptionsAnswersOld(id: any): any {
    var question;
    if (typeof id == 'number') {
      question = this.surveyResponse[id];
    } else if (typeof id == 'string') {
      question = this.surveyResponse[this.survey.shortnames[id]];
    }

    const checkboxAnswers: any[] = [];

    for (const optionId in question.options) {
      if (question.options.hasOwnProperty(optionId)) {
        const option = question.options[optionId];
        checkboxAnswers.push({
          id: option.id,
          option: option.option,
          answer: option.answer,
          original_answer: option.original_answer ? option.original_answer : undefined
        });
      }
    }

    return checkboxAnswers;
  }


  public getOptionsAnswers(value: any, selectedOnly: boolean = false): any[] {
  const id = this.getId(value);

  if (id === undefined || id === null) {
    return [];
  }

  // First look for a top-level question, then a nested subquestion.
  const question =
    this.surveyResponse?.[id] ??
    this.subquestionMap?.[id];

  if (!question?.options) {
    return [];
  }

  return Object.values(question.options)
    .filter((option: any) => {
      if (!selectedOnly) {
        return true;
      }

      return option.answer !== null &&
             option.answer !== undefined &&
             option.answer !== '';
    })
    .map((option: any) => ({
      id: option.id,
      option: option.option,
      answer: option.answer,
      original_answer: option.original_answer ?? undefined,
      selected:
        option.answer !== null &&
        option.answer !== undefined &&
        option.answer !== ''
    }));
}

  // Returns an array of radio answers from a radio grid that share the same parent.
  public getRadioButtonGrid(parentId: number): any {
    const radioButtonGrid: any = [];

    for (const key in this.surveyResponse) {
      if (this.surveyResponse.hasOwnProperty(key)) {
        const question = this.surveyResponse[key];
        if (question.parent === parentId && question.type === "RADIO") {
          radioButtonGrid.push(question);
        }
      }
    }

    return radioButtonGrid;
  }

  // --- Subquestion methods ---

  // Get a single subquestion's answer by id or shortname. getSurveyAnswer also
  // works (it falls back to subquestions) — use this when you want to be explicit.
  public getSubquestionAnswer(value: any): any {
    var id = this.getId(value);
    if (!id) return "subquestion not found.";

    var sq = this.subquestionMap[id];
    return sq ? sq.answer : "subquestion not found.";
  }

  // Get the full subquestion object (id, question, type, parent, answer).
  public getSubquestion(value: any): any {
    var id = this.getId(value);
    if (!id) return null;

    var sq = this.subquestionMap[id];
    if (!sq) return null;

    return {
      id: sq.id,
      question: sq.question,
      type: sq.type,
      parent: sq.parent,
      answer: sq.answer
    };
  }

  // Get all subquestions under a GROUP parent. Accepts id or shortname of the parent.
  public getSubquestions(parentValue: any): any[] {
    var parentId = this.getId(parentValue);
    if (!parentId || !this.surveyResponse[parentId]) return [];

    var parent = this.surveyResponse[parentId];
    if (!parent.subquestions) return [];

    return Object.values(parent.subquestions).map((sq: any) => ({
      id: sq.id,
      question: sq.question,
      type: sq.type,
      parent: sq.parent,
      answer: sq.answer
    }));
  }

  // Get subquestions merged with schema info from the survey definition.
  // Useful when the response's "question" field is just a placeholder label
  // ("Purpose", "Function/Role 1") and you want the richer schema title and shortname.
  public getSubquestionsWithSchema(parentValue: any): any[] {
    var parentId = this.getId(parentValue);
    if (!parentId) return [];

    var responseSubs = this.getSubquestions(parentId);
    var schemaParent = this.survey && this.survey.questions ? this.survey.questions[parentId] : null;
    var schemaSubs = schemaParent && schemaParent.sub_questions ? schemaParent.sub_questions : {};

    // Index schema subs by id
    var schemaById: { [key: string]: any } = {};
    for (const key in schemaSubs) {
      const sq = schemaSubs[key];
      if (sq && sq.id) schemaById[sq.id] = sq;
    }

    return responseSubs.map((sq) => {
      var schema = schemaById[sq.id];
      return Object.assign({}, sq, {
        shortname: schema ? schema.shortname : null,
        schemaTitle: schema ? schema.title : null,
        placeholder: schema && schema.properties ? schema.properties.placeholder : null
      });
    });
  }

  // Check whether a subquestion has a non-empty answer.
  public hasSubquestionAnswer(value: any): boolean {
    var id = this.getId(value);
    if (!id) return false;

    var sq = this.subquestionMap[id];
    if (!sq) return false;

    return sq.answer !== "" && sq.answer !== undefined && sq.answer !== null;
  }
}